import { type FC, useEffect, useState } from 'react'

import { MicOff } from '@liveHelper/assets/MicOff'
import { MicOn } from '@liveHelper/assets/MicOn'
import { ExclamationMark } from '@liveHelper/assets/YellowExclamationMark'
import {
  deviceButtonContainer,
  permissionBubble,
} from '@liveHelper/components/shared/shared.twind'
import { useMicrophonePermission } from '@liveHelper/hooks/useMicrophonePermission'
import {
  actionsSelector,
  callDataSelector,
  microphoneMutedSelector,
  paramsSelector,
  useStore,
  useStoreApi,
  visitorPrivateActionsSelector,
} from '@liveHelper/models/store'
import {
  MICROPHONE_BUTTON_MUTED,
  MICROPHONE_BUTTON_UNMUTED,
} from '@liveHelper/testIds'
import { ActionButton } from '@oneToOne/shared/styles'

import { useCheckMicrophonePermission } from '@core/devicePermissions/useCheckMicrophonePermission'
import { useContextTranslation } from '@core/i18n'
import { logMessage } from '@core/logging'
import { getGuestId } from '@core/tracking/guestId'
import { useTwindContext } from '@core/twind'

type WarningMessage = 'muted' | 'permission' | undefined

interface MicButtonProps {
  isMinimized?: boolean
}

/**
 * A mic button component for toggling microphone mute/unmute state.
 */
export const MicButton: FC<MicButtonProps> = ({ isMinimized }) => {
  const { tw } = useTwindContext()
  const { t } = useContextTranslation()

  const params = useStore(paramsSelector)
  const microphoneMuted = useStore(microphoneMutedSelector)
  const callData = useStore(callDataSelector)
  const { microphoneMutedStateToggled } = useStore(actionsSelector)
  const { sendMicrophoneStatus } = useStore(visitorPrivateActionsSelector)

  const storeApi = useStoreApi()
  const guestId = getGuestId()
  const permission = useMicrophonePermission()

  const [permissionMessage, setPermissionMessage] =
    useState<WarningMessage>(undefined)
  const [showWarningMessage, setShowWarningMessage] = useState<boolean>(false)

  useEffect(() => {
    if (callData?.agentId && sendMicrophoneStatus) {
      // Send microphone status every 5 seconds
      // because the agent might have refreshed his/her page during the call
      sendMicrophoneStatus({
        isMuted: microphoneMutedSelector(storeApi.getState()),
      })
      const interval = setInterval(() => {
        sendMicrophoneStatus({
          isMuted: microphoneMutedSelector(storeApi.getState()),
        })
      }, 5000)

      return () => clearInterval(interval)
    }
  }, [callData?.agentId, sendMicrophoneStatus, storeApi])

  const { initiateMicrophoneAccess } = useCheckMicrophonePermission({
    onAllowed: () => {
      setShowWarningMessage(false)
      microphoneMutedStateToggled()
    },
    onRejected: () => {
      setShowWarningMessage(false)
      setPermissionMessage('permission')
      logMessage('Microphone permission rejected', {
        data: {
          visitor_id: guestId,
          chat_channel_id: params.chat_channel_id,
        },
      })
    },
    onDismissed: () => {
      setShowWarningMessage(false)
      setPermissionMessage('permission')
      logMessage('Microphone permission dismissed', {
        data: {
          visitor_id: guestId,
          chat_channel_id: params.chat_channel_id,
        },
      })
    },
  })

  useEffect(() => {
    let timeout
    if (permission === 'granted') {
      if (microphoneMuted) {
        timeout = setTimeout(() => {
          setPermissionMessage('muted')
        }, 2000)
      } else {
        setPermissionMessage(undefined)
      }
    } else {
      if (permission === 'prompt' || permission === 'denied') {
        setPermissionMessage('permission')
        if (!microphoneMuted) {
          microphoneMutedStateToggled()
        }
      }
    }
    return () => {
      timeout && clearTimeout(timeout)
    }
  }, [microphoneMuted, microphoneMutedStateToggled, permission])

  const onClick = () => {
    if (microphoneMuted) {
      if (permissionMessage === 'permission') {
        setShowWarningMessage(true)
      }
      initiateMicrophoneAccess()
    } else {
      microphoneMutedStateToggled()
    }
  }

  return (
    <div className={tw?.(deviceButtonContainer)}>
      {showWarningMessage || permissionMessage === 'muted' ? (
        <div
          className={tw?.(
            permissionBubble,
            'absolute',
            'text-base',
            'italic',
            'rounded',
            'py-1',
            'px-2',
            'mb-5',
          )}
        >
          {permissionMessage === 'muted'
            ? t('oneToOne:You are muted')
            : t('oneToOne:Permission required')}
        </div>
      ) : null}
      <ActionButton
        data-testid={
          microphoneMuted ? MICROPHONE_BUTTON_MUTED : MICROPHONE_BUTTON_UNMUTED
        }
        aria-label={
          microphoneMuted
            ? t(`oneToOne:Unmute Microphone`)
            : t(`oneToOne:Mute Microphone`)
        }
        buttonSize={isMinimized ? 'md' : 'lg'}
        actionType={microphoneMuted ? 'danger' : 'neutral'}
        shape="circle"
        onClick={onClick}
        icon={
          microphoneMuted ? (
            <MicOff
              width={isMinimized ? '18px' : '26px'}
              fill="var(--white)"
              label=""
              aria-hidden
            />
          ) : (
            <MicOn
              width={isMinimized ? '18px' : '26px'}
              fill="var(--white)"
              label=""
              aria-hidden
            />
          )
        }
      >
        {permissionMessage === 'permission' ? (
          <ExclamationMark
            className={tw?.('absolute', 'top-0', 'right-0')}
            label={t('oneToOne:Permission required')}
          />
        ) : null}
      </ActionButton>
    </div>
  )
}
